import React, { useId } from "react";
import { Button, Modal } from "react-bootstrap";

import "./ui.css";

const buttonVariants = {
  danger: "danger",
  warning: "warning",
  primary: "primary",
  info: "primary",
};

const ConfirmIcon = ({ tone }) => (
  <span className={`rg-ui-confirm-dialog__icon rg-ui-confirm-dialog__icon--${tone}`} aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5h2v7h-2V7Zm1 11a1.2 1.2 0 1 1 0-2.4A1.2 1.2 0 0 1 12 18Z" />
    </svg>
  </span>
);

/** Accessible confirmation modal with a cancel-first focus order. */
const ConfirmDialog = ({
  show = false,
  open,
  onCancel,
  onConfirm,
  title = "Confirm action",
  message,
  children,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  confirmVariant,
  icon,
  isConfirming = false,
  loadingLabel = "Working…",
  confirmDisabled = false,
  hideCloseButton = false,
  backdrop = true,
  keyboard = true,
  centered = true,
  className = "",
}) => {
  const generatedId = useId().replace(/:/g, "");
  const titleId = `rg-ui-confirm-title-${generatedId}`;
  const descriptionId = `rg-ui-confirm-description-${generatedId}`;
  const normalizedTone = buttonVariants[tone] ? tone : "danger";
  const isOpen = typeof open === "boolean" ? open : Boolean(show);
  const body = children || message;

  const handleCancel = () => {
    if (!isConfirming && onCancel) onCancel();
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleCancel}
      centered={centered}
      backdrop={isConfirming ? "static" : backdrop}
      keyboard={!isConfirming && keyboard}
      aria-labelledby={titleId}
      aria-describedby={body ? descriptionId : undefined}
      dialogClassName={["rg-ui-confirm-dialog", className].filter(Boolean).join(" ")}
      restoreFocus
    >
      <Modal.Header closeButton={!hideCloseButton && !isConfirming}>
        <Modal.Title as="h2" id={titleId}>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="rg-ui-confirm-dialog__body">
          {icon === false ? null : icon || <ConfirmIcon tone={normalizedTone} />}
          {body && (
            <div id={descriptionId} className="rg-ui-confirm-dialog__message">
              {body}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleCancel} disabled={isConfirming} autoFocus>
          {cancelLabel}
        </Button>
        <Button
          variant={confirmVariant || buttonVariants[normalizedTone]}
          onClick={onConfirm}
          disabled={isConfirming || confirmDisabled}
          aria-busy={isConfirming}
        >
          {isConfirming && <span className="rg-ui-confirm-dialog__spinner" aria-hidden="true" />}
          {isConfirming ? loadingLabel : confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
